/**
 * Dealer Management Page
 * Allows manufacturers to manage their distribution network (dealers, sub-dealers, retailers)
 * No login required for dealers - they use OTP verification when accepting packages
 */

import { useEffect, useState } from 'react';
import { dealerService, Dealer } from '../services/dealer.service';
import { Alert, Button, FilterBar, Input, Modal, PageHeader, StatusBadge } from '../components/ui';

interface AddDealerForm {
  fullName: string;
  businessName: string;
  phone: string;
  address: string;
  email?: string; // Optional
}

export default function UserManagementPage() {
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Add dealer modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingDealer, setAddingDealer] = useState(false);
  const [addDealerForm, setAddDealerForm] = useState<AddDealerForm>({
    fullName: '',
    businessName: '',
    phone: '',
    address: '',
    email: '',
  });



  // Filter state
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadDealers();
  }, []);

  const loadDealers = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await dealerService.getDealerNetwork();
      if (result.success && result.dealers) {
        setDealers(result.dealers);
      } else {
        setError(result.error || 'Failed to load dealers');
      }
    } catch (err: any) {
      console.error('Failed to load dealers:', err);
      setError(err.response?.data?.error || 'Failed to load dealers');
    } finally {
      setLoading(false);
    }
  };



  const handleAddDealer = async () => {
    try {
      setAddingDealer(true);
      setError(null);

      console.log('Form data being submitted:', addDealerForm);

      // Validate form
      const missingFields = [];
      if (!addDealerForm.fullName?.trim()) missingFields.push('Full Name');
      if (!addDealerForm.phone?.trim()) missingFields.push('Phone Number');
      if (!addDealerForm.address?.trim()) missingFields.push('Address');
      
      if (missingFields.length > 0) {
        console.log('Missing fields:', missingFields);
        setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
        return;
      }

      const result = await dealerService.addDealer({
        name: addDealerForm.fullName,
        businessName: addDealerForm.businessName,
        mobileNumber: addDealerForm.phone,
        address: addDealerForm.address,
        email: addDealerForm.email,
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      setSuccess(`${addDealerForm.fullName} added successfully!`);
      setShowAddModal(false);
      setAddDealerForm({
        fullName: '',
        businessName: '',
        phone: '',
        address: '',
        email: '',
      });
      
      // Reload dealers
      loadDealers();
    } catch (err: any) {
      console.error('Failed to add dealer:', err);
      setError(err.response?.data?.error || 'Failed to add dealer');
    } finally {
      setAddingDealer(false);
    }
  };

  const handleRemoveDealer = async (dealerId: string, dealerName: string) => {
    if (!confirm(`Are you sure you want to remove ${dealerName} from your network?`)) {
      return;
    }

    try {
      const result = await dealerService.removeDealer(dealerId);
      if (result.success) {
        setSuccess(`${dealerName} removed from network`);
        loadDealers();
      } else {
        setError(result.error || 'Failed to remove dealer');
      }
    } catch (err: any) {
      console.error('Failed to remove dealer:', err);
      setError(err.response?.data?.error || 'Failed to remove dealer');
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter dealers based on search term
  const filteredDealers = dealers.filter(dealer => {
    if (searchTerm === '') return true;
    
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      (dealer.name && dealer.name.toLowerCase().includes(searchLower)) ||
      (dealer.businessName && dealer.businessName.toLowerCase().includes(searchLower)) ||
      (dealer.mobileNumber && dealer.mobileNumber.includes(searchTerm)) ||
      (dealer.address && dealer.address.toLowerCase().includes(searchLower)) ||
      (dealer.email && dealer.email.toLowerCase().includes(searchLower));
    
    return matchesSearch;
  });



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dealers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <PageHeader
        title="Dealer Management"
        description="Manage your distribution network: dealers, sub-dealers, and retailers."
        actions={
          <Button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Dealer
          </Button>
        }
      />

        {/* Alerts */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-6">
            {success}
          </Alert>
        )}

      {/* Filters */}
      <FilterBar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search dealers by name, business name, mobile number, or address..."
      />

        {/* Dealers List */}
        {filteredDealers.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Dealers Found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm 
                ? 'No dealers match your search.' 
                : 'Start building your distribution network by adding dealers.'
              }
            </p>
            <Button
              onClick={() => setShowAddModal(true)}
              className="mt-4"
            >
              Add First Dealer
            </Button>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Network Dealers ({filteredDealers.length})
              </h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200" aria-label="Dealer network">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dealer
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact & Location
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Added
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDealers.map((dealer) => (
                    <tr key={dealer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{dealer.name}</div>
                          {dealer.businessName && <div className="text-sm text-gray-600">{dealer.businessName}</div>}
                          <div className="text-sm text-gray-500">{dealer.mobileNumber}</div>
                          {dealer.email && <div className="text-sm text-gray-500">{dealer.email}</div>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {dealer.address && <div>{dealer.address}</div>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge tone="active" size="sm">
                          Dealer
                        </StatusBadge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(dealer.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleRemoveDealer(dealer.id, dealer.name)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {/* Add Dealer Modal */}
      {showAddModal && (
        <Modal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setAddDealerForm({
              fullName: '',
              businessName: '',
              phone: '',
              address: '',
              email: '',
            });
            setError(null);
          }}
          title="Add New Dealer"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dealer Name *
                </label>
                <Input
                  value={addDealerForm.fullName}
                  onChange={(e) => setAddDealerForm({ ...addDealerForm, fullName: e.target.value })}
                  placeholder="Enter dealer name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Name
                </label>
                <Input
                  value={addDealerForm.businessName}
                  onChange={(e) => setAddDealerForm({ ...addDealerForm, businessName: e.target.value })}
                  placeholder="Enter business name (optional)"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number *
              </label>
              <Input
                value={addDealerForm.phone}
                onChange={(e) => setAddDealerForm({ ...addDealerForm, phone: e.target.value })}
                placeholder="Enter mobile number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address *
              </label>
              <Input
                value={addDealerForm.address}
                onChange={(e) => setAddDealerForm({ ...addDealerForm, address: e.target.value })}
                placeholder="Enter complete address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email (Optional)
              </label>
              <Input
                type="email"
                value={addDealerForm.email}
                onChange={(e) => setAddDealerForm({ ...addDealerForm, email: e.target.value })}
                placeholder="Enter email address (optional)"
              />
            </div>



            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                The dealer will be added to your network. When you assign packages to them, they'll receive an OTP on their registered mobile number for verification.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddModal(false);
                  setAddDealerForm({
                    fullName: '',
                    businessName: '',
                    phone: '',
                    address: '',
                    email: '',
                  });
                  setError(null);
                }}
                disabled={addingDealer}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddDealer}
                disabled={addingDealer}
              >
                {addingDealer ? 'Adding Dealer...' : 'Add Dealer'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
